import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { InsightsIcon, PricingIcon, StarCircled, VenueIcon } from '@weareopenr/component-library';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis, Legend } from 'recharts';
import type { ITenantSummary } from '../types';
import { fixtureAwsCost } from '../mocks/fixtures';
import { STATUS } from './palette';
import { useTheme } from '../theme';
import { AutoSize } from './AutoSize';
import { ChartTooltip, dollars, money } from './tooltip';
import styles from './widgets.module.scss';

const useInk = () => {
  const { theme } = useTheme();
  const { ink } = theme;
  const axisProps = {
    tick: { fill: ink.secondary, fontSize: 11 },
    axisLine: false,
    tickLine: false,
  } as const;
  return { INK: ink, SERIES: ink.series, axisProps };
};

const shortDay = (iso: string): string => {
  const date = new Date(iso);
  return `${date.getUTCDate()}/${date.getUTCMonth() + 1}`;
};

export const KpiStrip = ({ tenants }: { tenants: ITenantSummary[] }) => {
  const active = tenants.filter(({ status }) => status === 'active').length;
  const venues = tenants.reduce((total, { venues: count }) => total + (count ?? 0), 0);
  const mrr = tenants.reduce((total, { mrr: value }) => total + (value ?? 0), 0);
  const failing = tenants.filter(({ health }) => health === 'error').length;
  const tiles: {
    label: string;
    value: string;
    to?: string;
    tone?: 'bad' | 'good';
    hint: string;
    Icon: typeof StarCircled;
  }[] = [
    {
      label: 'Active tenants',
      value: String(active),
      to: '/tenants',
      hint: `${tenants.length - active} not active`,
      Icon: StarCircled,
    },
    {
      label: 'Venues',
      value: venues.toLocaleString('en-GB'),
      hint: 'across every tenant',
      Icon: VenueIcon,
    },
    {
      label: 'Monthly recurring',
      value: money(mrr),
      to: '/billing',
      hint: 'from subscriptions',
      Icon: PricingIcon,
    },
    {
      label: 'Failing tenants',
      value: String(failing),
      tone: failing > 0 ? 'bad' : 'good',
      hint: failing > 0 ? 'failures in the last 24h' : 'no failures in 24h',
      Icon: InsightsIcon,
    },
  ];
  return (
    <div className={styles.kpiStrip}>
      {tiles.map(({ label, value, to, tone, hint, Icon }) => (
        <div key={label} className={styles.kpi} data-tone={tone}>
          <span className={styles.kpiIcon}>
            <Icon />
          </span>
          <span className={styles.kpiLabel}>{label}</span>
          <span className={styles.kpiValue}>{value}</span>
          <span className={styles.kpiHint}>{hint}</span>
          {to ? (
            <Link to={to} className={styles.kpiLink}>
              View →
            </Link>
          ) : null}
        </div>
      ))}
    </div>
  );
};

export const AwsCostTrend = () => {
  const { INK, SERIES, axisProps } = useInk();
  const data = useMemo(() => fixtureAwsCost(), []);
  const keys: {
    key: keyof ReturnType<typeof fixtureAwsCost>[number];
    name: string;
    colour: string;
  }[] = [
    { key: 'prod', name: 'Prod', colour: SERIES[0] },
    { key: 'staging', name: 'Staging', colour: SERIES[1] },
    { key: 'dev', name: 'Dev', colour: SERIES[2] },
    { key: 'other', name: 'Transit + mgmt', colour: SERIES[3] },
  ];
  return (
    <AutoSize>
      {({ width, height }) => (
        <BarChart
          width={width}
          height={height}
          data={data}
          margin={{ top: 8, right: 8, bottom: 0, left: -12 }}
          barCategoryGap={14}
        >
          <CartesianGrid vertical={false} stroke={INK.grid} />
          <XAxis dataKey="month" {...axisProps} />
          <YAxis {...axisProps} tickFormatter={(value) => `$${Number(value) / 1000}k`} />
          <Tooltip cursor={{ fill: INK.grid }} content={<ChartTooltip format={dollars} />} />
          <Legend
            iconType="circle"
            iconSize={8}
            wrapperStyle={{ fontSize: 12, color: INK.secondary }}
          />
          {keys.map(({ key, name, colour }, index) => (
            <Bar
              isAnimationActive
              animationDuration={500}
              animationEasing="ease-out"
              key={key}
              dataKey={key}
              name={name}
              stackId="cost"
              fill={colour}
              stroke={INK.surface}
              strokeWidth={1}
              radius={index === keys.length - 1 ? [4, 4, 0, 0] : 0}
              maxBarSize={40}
            />
          ))}
        </BarChart>
      )}
    </AutoSize>
  );
};

export const HealthSummary = ({ tenants }: { tenants: ITenantSummary[] }) => {
  const buckets: { key: ITenantSummary['health']; label: string }[] = [
    { key: 'ok', label: 'Healthy' },
    { key: 'warning', label: 'Needs attention' },
    { key: 'error', label: 'Failing' },
    { key: 'unknown', label: 'Unknown' },
  ];
  const attention = tenants.filter(({ health }) => health === 'error' || health === 'warning');
  return (
    <div className={styles.health}>
      <div className={styles.healthRow}>
        {buckets.map(({ key, label }) => (
          <div key={key} className={styles.healthBucket}>
            <span className={styles.healthDot} style={{ background: STATUS[key] }} />
            <span className={styles.healthCount}>
              {tenants.filter(({ health }) => health === key).length}
            </span>
            <span className={styles.healthLabel}>{label}</span>
          </div>
        ))}
      </div>
      <ul className={styles.attention}>
        {attention.length === 0 ? <li className={styles.quiet}>Nothing needs attention.</li> : null}
        {attention.map(({ id, name: key, display_name: name, health, last_publish_at: last }) => (
          <li key={id}>
            <Link to={`/tenants/${key}`} className={styles.attentionItem}>
              <span className={styles.healthDot} style={{ background: STATUS[health] }} />
              <span className={styles.attentionName}>{name}</span>
              <span className={styles.attentionMeta}>
                {health === 'error'
                  ? 'failures in the last 24h'
                  : last
                  ? `last publish ${shortDay(last)}`
                  : 'no publish yet'}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};
